import * as partsConstants from '../../utils/partsToShow/partsConstants';
import * as sidebarSectionConstants from '../../utils/partsToShow/sidebarSectionConstants';
import { getSideMenuList } from './sideMenuList';

/**
 * Helper function to check if a master category should be shown based on permission flags
 * @param category - The master category (e.g., 'CUSTOMER', 'VENDOR', 'SUPPLIER')
 * @param permissions - The permissions object containing permission flags
 * @returns boolean indicating if the category should be shown (true if permission is undefined/missing, true if explicitly true, false if explicitly false)
 */
const hasMasterCategoryPermission = (
  category: string,
  permissions: Record<string, boolean> | undefined
): boolean => {
  // If permissions object doesn't exist, allow showing (backward compatibility)
  if (!permissions) {
    return true;
  }
  // Map VENDOR to FF for permission check (VENDOR is stored as FF in backend)
  const permissionCategory = category === 'FF' ? 'VENDOR' : category;
  const permissionKey = `MASTERS_${permissionCategory}_VIEW`;
  const permissionValue = permissions[permissionKey];

  // If permission is not defined, allow showing (backward compatibility for categories not in UI)
  if (permissionValue === undefined) {
    return true;
  }

  // Only hide if explicitly set to false
  return permissionValue === true;
};


/**
 * Helper function to check if a user has edit permission for a master category
 * @param category - The master category (e.g., 'CUSTOMER', 'VENDOR', 'SUPPLIER', 'ADDRESS')
 * @param permissions - The permissions object containing permission flags
 * @returns boolean indicating if the user has edit permission (false if permission is undefined/missing or false)
 */
export const hasMasterCategoryEditPermission = (
  category: string,
  permissions: Record<string, boolean> | undefined
): boolean => {
  // If permissions object doesn't exist, deny edit (safer default)
  if (!permissions) {
    return false;
  }
  // Map FF to VENDOR for permission check (VENDOR is stored as FF in UI)
  const permissionCategory = category === 'FF' ? 'VENDOR' : category;
  const permissionKey = `MASTERS_${permissionCategory}_EDIT`;
  return permissions[permissionKey] === true;
};


export const getAuthorizationConfig = (modules, userData, show_task_workflow, permissions) => {
  if (!modules || !userData) {
    return null;
  }

  // Helper to check both show_master_categories and permission flags
  const shouldShowMasterCategory = (category: string): boolean => {
    const hasModuleCategory = modules.show_master_categories?.includes(category);
    const hasPermission = hasMasterCategoryPermission(category, permissions);
    // Show if module category exists AND (permission is undefined OR permission is true)
    return hasModuleCategory && hasPermission;
  };

  const partsToShow = {
    [partsConstants.DASHBOARD]: modules.show_dashboards,
    // [partsConstants.SUMMARY_DASHBOARD]: modules.show_summary_dashboard,
    [partsConstants.PLAY]: modules.show_play,
    [partsConstants.EXCEPTION_RULES]: modules.show_exception_rules,
    [partsConstants.EXCEPTION_ALERT_INSTANCES]: modules.show_exception_alert_instances,
    [partsConstants.EXCEPTION_ALERT_DEFINITIONS]: modules.show_exception_alert_definitions,
    [partsConstants.CONTAINER_TRACKING]:
      modules.show_shipping_management && modules.show_container_tracking,
    [partsConstants.IMPORTS_CONTAINER_TRACKING]: modules.show_container_tracking_imports,
    [partsConstants.EXPORTS_CONTAINER_TRACKING]: modules.show_container_tracking_exports,
    [partsConstants.INQUIRY_SHIPPER]: modules.show_inquiry,
    [partsConstants.INQUIRY_FF]: modules.show_inquiry_bidding,
    [partsConstants.EXPORTS_RAKE]: modules.show_rake_exports,
    [partsConstants.EXPORTS_SALES_ORDER]: modules.show_exports_sales_order,
    [partsConstants.IMPORTS_RAKE]: modules.show_rake_imports,
    [partsConstants.IMPORTS_SALES_ORDER]: modules.show_imports_sales_order,
    [partsConstants.SETUP_USERS]: modules.show_user_management,
    [partsConstants.SETUP_TEAMS]: true,
    // [partsConstants.SETUP_MASTERS]: modules.show_masters,
    [partsConstants.SETUP_PERMISSIONS]: modules.show_setup_permissions,
    [partsConstants.SETUP_TNC]: modules.show_rfq_rfq_plans,
    [partsConstants.SETUP_CHARGE_MASTER]: modules.show_rfq_rfq_plans,
    [partsConstants.RFQ_SHIPPER]: modules.show_rfq,
    [partsConstants.RFQ_SHIPPER_OCEAN_FREIGHT]: modules.show_rfq_rfq_plans,
    [partsConstants.RFQ_RATE_MASTER]: modules.show_rfq_rfq_plans,
    // [partsConstants.RFQ_BULK_REQUEST]: modules.show_rfq_rfq_plans || modules.show_bid_bulk_upload || modules.show_bidding,
    [partsConstants.RFQ_SHIPPER_ANALYTICS]: modules.show_rfq_analytics,
    [partsConstants.RFQ_SHIPPING_LINE]: modules.show_bidding,
    [partsConstants.RFQ_VENDOR]: modules.show_bidding,
    [partsConstants.IMPORTS]: modules.show_imports,
    [partsConstants.EXPORTS]: modules.show_shipping_management && modules.show_exports,
    [partsConstants.AIR_CARGO]: modules.show_air_tracking,
    [partsConstants.TRIANGULATION]: modules.show_container_triangulation,
    [partsConstants.DOMESTIC_TRACKING]: modules.show_domestic_tracking,
    [partsConstants.DOMESTIC_CARRIER_MANAGEMENT]: modules.show_domestic_carrier_management,
    [partsConstants.CUSTOMER_ACTIVITY]: modules.show_domestic_tracking,
    [partsConstants.MY_PROFILE]: modules.show_my_profile,
    [partsConstants.MY_ORGANISATION]: true,
    [partsConstants.MY_FF]: shouldShowMasterCategory('FF'),
    [partsConstants.MY_CHA]: shouldShowMasterCategory('CHA'),
    [partsConstants.MY_SHIPPER]: shouldShowMasterCategory('SHIPPER'),
    [partsConstants.MY_CHILD]: shouldShowMasterCategory('CHILD'),
    [partsConstants.MY_SHIPPING_LINE]: shouldShowMasterCategory('SHIPPING_LINE'),
    [partsConstants.MY_DOCUMENTS]: shouldShowMasterCategory('DOCUMENT'),
    [partsConstants.REGIONS]: modules.show_rfq_rfq_plans,
    [partsConstants.MY_CUSTOMER]: userData.type !== 'FF' && shouldShowMasterCategory('CUSTOMER'),
    [partsConstants.MY_SUPPLIER]: userData.type !== 'FF' && shouldShowMasterCategory('SUPPLIER'),
    [partsConstants.PRODUCT]: shouldShowMasterCategory('PRODUCT'),
    [partsConstants.ADDRESS]: modules.enable_address_master_in_menu && shouldShowMasterCategory('ADDRESS'),
    [partsConstants.LOGOUT]: true,
    [partsConstants.PAYABLE]: modules.show_invoice_payable,
    [partsConstants.RECEIVABLE]: modules.show_invoice_receivable,
    [partsConstants.RATE_MASTER]: modules.show_rate_master,
    [partsConstants.BULK_REQUEST]:
      modules.show_rate_master ||
      modules.show_rfq_rfq_plans ||
      modules.show_bid_bulk_upload ||
      modules.show_bidding ||
      ['CUSTOMER', 'SUPPLIER', 'SHIPPER'].some((category) => shouldShowMasterCategory(category)),
    [partsConstants.DISCOVER_BUY_RATES]: modules.show_discover_buy_rates,
    [partsConstants.DISCOVER_RATES]: modules.show_discover_buy_rates,
    [partsConstants.DISCOVER_SELL_RATES]: modules.show_discover_sell_rates,
    [partsConstants.MARKUP_AUTOMATION_TOOL]: modules.show_markup_criteria,
    [partsConstants.DSR_TEMPLATES]: modules.show_master_categories?.includes('DSR_TEMPLATE'),
    [partsConstants.DEMURRAGE_AND_DETENTION]: modules.show_dnd_configuration,
    [partsConstants.VESSEL_SCHEDULES]: modules.show_vessel_schedules,
    [partsConstants.SHIP_EXEC_SHIPPER]: modules.show_shipment_execution_shipper,
    [partsConstants.SHIP_EXEC_VENDOR]: modules.show_shipment_execution_vendor,
    [partsConstants.INQUIRY_FROM_PROCURE]: modules.inquiry_creation_from_procure,
    [partsConstants.MY_SUBSCRIPTION_PLAN]: modules.show_subscription_plan,
    [partsConstants.TASKS_WORKFLOW]: show_task_workflow,
    // Approval Policies is NOT a master/module — it's access-gated. Org enables the feature via
    // shipper_modules.show_approval_policies; visibility then depends on the user's role:
    //   - ADMIN sees it implicitly (no per-user grant needed).
    //   - Non-admin sees it only when the APPROVAL_CONFIG_VIEW permission is present in
    //     user_permission (granted via the toggle in the user create/edit modal).
    [partsConstants.APPROVAL_POLICIES]:
      !!modules.show_approval_policies &&
      (userData?.role === 'ADMIN' || (permissions && permissions.APPROVAL_CONFIG_VIEW === true)),
    // My Approvals is the approver inbox (runtime side of the approval engine). It is gated ORG-ONLY:
    // every user in an org that has the approval feature on sees their own approvals — no role or
    // per-user permission check, unlike Approval Policies above which also gates on APPROVAL_CONFIG_VIEW.
    [partsConstants.MY_APPROVALS]: !!modules.show_approval_policies,
    // for Api
    [partsConstants.API]: modules.give_api_access,
    [partsConstants.ANALYTICS]: modules.show_analytics,

    [partsConstants.CUSTOM_CONFIG]: modules.show_custom_config,
    [partsConstants.PORT_GROUPS]: modules.show_port_groups,
  };
  return partsToShow;
};

export const checkVisibleOptions = (options, partsToShow) =>
  options.filter((option: any) => partsToShow[option]);

export const checkIfDropdownExist = (options, partsToShow) => {
  const visibleOptions = checkVisibleOptions(options, partsToShow);
  return visibleOptions && visibleOptions.length >= 2 ? true : false;
};

export const getSectionsToShow = (partsToShow: any) => {
  const sideMenuList = getSideMenuList;
  const sectionsToShow: any = {
    [sidebarSectionConstants.DASHBOARD]: false,
    [sidebarSectionConstants.PLAY]: false,
    [sidebarSectionConstants.EXCEPTION_RULES]: false,
    [sidebarSectionConstants.EXCEPTION_ALERTS]: false,
    [sidebarSectionConstants.SHIPPING_MANAGEMENT]: false,
    [sidebarSectionConstants.RFQ]: false,
    [sidebarSectionConstants.BIDDING]: false,
    [sidebarSectionConstants.NEW_EXPORTS]: false,
    [sidebarSectionConstants.NEW_IMPORTS]: false,
    [sidebarSectionConstants.DOMESTIC]: false,
    [sidebarSectionConstants.SETUP]: false,
    [sidebarSectionConstants.MY_ACCOUNT]: false,
  };
  sideMenuList.forEach((sideMenuElem: any) => {
    if (sideMenuElem.id !== 'root') {
      sectionsToShow[sideMenuElem.id] = checkIfDropdownExist(sideMenuElem.options, partsToShow);
    }
  });
  return sectionsToShow;
};
